import { useEffect, useState } from "react";
import { useUser } from "src/shared/context/UserContext";
import { getUsersFromIndexedDB } from "./indexDB/indexedDB";

export interface DropdownUser {
  id: string;
  userName: string; // This is the full name (firstName + middleName + lastName)
  loginName: string; // This is the username (e.g., "gobikhanuser")
}

export const getUserId = (user: Record<string, any>): string => String(user?._id?.$oid || user?._id || user?.id || "");

export const getUserName = (user: Record<string, any>): string => [user?.firstName, user?.middleName, user?.lastName].filter(Boolean).join(" ");

export const getLoginName = (user: Record<string, any>): string => String(user?.userName || "");

export const useUserHelpers = () => {
  const { user } = useUser();

  const [users, setUsers] = useState<DropdownUser[]>([]);

  const loginUser = user?.role?.[0] === "User";

  useEffect(() => {
    if (loginUser || !user?.orgId) {
      setUsers([]);
      return;
    }

    const loadUsers = async () => {
      try {
        const storedUsers = await getUsersFromIndexedDB();

        const dropdownUsers: DropdownUser[] = storedUsers
          .filter((storedUser) => storedUser.orgId === user.orgId)
          .map((storedUser) => ({
            id: getUserId(storedUser),
            userName: getUserName(storedUser), // Full name
            loginName: getLoginName(storedUser), // Username
          }))
          .filter((dropdownUser) => Boolean(dropdownUser.id));

        setUsers(dropdownUsers);
      } catch (error) {
        console.error("Unable to load users from IndexedDB:", error);
        setUsers([]);
      }
    };

    loadUsers();
  }, [loginUser, user?.orgId]);

  /**
   * Get login name using user ID or login name
   */
  const getLoginNameById = (identifier: string | number | undefined | null): string => {
    if (!identifier) return "";

    const stringId = String(identifier);

    // Try by ID first
    let foundUser = users.find((item) => item.id === stringId);

    // If not found by ID, try by loginName
    if (!foundUser) {
      foundUser = users.find((item) => item.loginName === stringId);
    }

    return foundUser?.loginName || "";
  };

  /**
   * Get display name using user ID or login name
   * ✅ Returns userName (full name), with fallback to identifier
   */
  const getUserNameById = (identifier: string | number | undefined | null): string => {
    if (!identifier) return "";

    const stringId = String(identifier);

    // Try by ID first
    let foundUser = users.find((item) => item.id === stringId);

    // If not found by ID, try by loginName
    if (!foundUser) {
      foundUser = users.find((item) => item.loginName === stringId);
    }

    // 🔥 If still not found, check if it's the current user
    if (!foundUser && user) {
      const currentUserLoginName = getLoginName(user);
      if (currentUserLoginName === stringId) {
        return getUserName(user); // Return current user's full name
      }
    }

    return foundUser?.userName || stringId;
  };

  return {
    users,
    loginUser,
    getLoginNameById,
    getUserNameById,
  };
};
